import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import {
  addOrMergePrintBundle,
  createEmptyPrintQueue,
  createPrintQueueBundle,
  getOrderedQueueItems,
  getQueueOrderKey,
  moveQueueEntry,
  prependPrintBundle,
  PRINT_QUEUE_AUTO_CLEAR_MS,
  PRINT_QUEUE_ITEM_STATES,
  removeQueueBundle,
  restorePrintQueueState,
  toPersistablePrintSnapshot,
  updateQueueItem,
} from '../services/printQueueModel';
import {
  clearPrintQueueStorage,
  deletePrintQueueBlob,
  cleanupStaleTemporaryPrintQueueBlobs,
  getPrintQueueStorageEstimate,
  loadPrintQueueState,
  savePrintQueueState,
} from '../services/printQueueStore';
import { materializePrintQueueItem } from '../services/printQueueMaterializers';
import qzPrintExecutor from '../services/qzPrintExecutor';
import {
  diagnoseQzProfiles,
  getQzPrintReadiness,
  getDefaultQzSettings,
  withGiftCardTrayRotation,
} from '../services/qzPrintService';
import {
  groupOrdersForPrintSeparators,
  groupItemsForPrintSeparators,
} from '../utils/printJobSeparators';
import { recordPackingSlipPrintCounts } from '../services/packingSlipPrintCountService';
import { recordConfirmedPrintQueueBundle } from '../services/printQueueAuditService';
import { withNormalizedPackingSlipMargins } from '../utils/packingSlipHtml';
import {
  coercePackingSlipFormat,
  isPackingSlipFormat,
  PACKING_SLIP_FORMAT,
} from '../utils/packingSlipPrintDefaults';

const PrintQueueContext = createContext(null);

export function PrintQueueProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState(createEmptyPrintQueue);
  const [initialized, setInitialized] = useState(false);
  const [storageEstimate, setStorageEstimate] = useState({ usage: 0, quota: 0, ratio: 0 });
  const [persistenceWarning, setPersistenceWarning] = useState('');
  const stateRef = useRef(state);
  const autoClearTimersRef = useRef(new Map());
  const confirmBundleRef = useRef(async () => {});

  const clearAutoClearTimer = useCallback((bundleId) => {
    const timer = autoClearTimersRef.current.get(bundleId);
    if (timer) {
      clearTimeout(timer);
      autoClearTimersRef.current.delete(bundleId);
    }
  }, []);

  const scheduleBundleAutoClear = useCallback(
    (bundleId) => {
      if (!bundleId) return;
      clearAutoClearTimer(bundleId);
      const timer = setTimeout(async () => {
        autoClearTimersRef.current.delete(bundleId);
        if (stateRef.current.running) {
          scheduleBundleAutoClear(bundleId);
          return;
        }
        if (stateRef.current.blockedItemId) return;
        const bundle = stateRef.current.bundles.find((entry) => entry.id === bundleId);
        if (!bundle?.items?.length) return;
        const ready = bundle.items.every((item) =>
          [
            PRINT_QUEUE_ITEM_STATES.AWAITING_CONFIRMATION,
            PRINT_QUEUE_ITEM_STATES.SKIPPED,
            PRINT_QUEUE_ITEM_STATES.CANCELED,
          ].includes(item.status),
        );
        if (!ready) return;
        try {
          await confirmBundleRef.current(bundleId);
        } catch (error) {
          console.warn('[PrintQueue] auto-clear failed:', error);
        }
      }, PRINT_QUEUE_AUTO_CLEAR_MS);
      autoClearTimersRef.current.set(bundleId, timer);
    },
    [clearAutoClearTimer],
  );

  const commit = useCallback((updater) => {
    const next =
      typeof updater === 'function' ? updater(stateRef.current) : updater;
    stateRef.current = next;
    setState(next);
    savePrintQueueState(next).catch(() =>
      setPersistenceWarning(
        'The print queue could not be saved. Keep this tab open until printing is complete.',
      ),
    );
    return next;
  }, []);

  useEffect(() => {
    let active = true;
    cleanupStaleTemporaryPrintQueueBlobs().catch(() => {});
    Promise.all([loadPrintQueueState(), getPrintQueueStorageEstimate()])
      .then(([stored, estimate]) => {
        if (!active) return;
        const restored = restorePrintQueueState(stored);
        stateRef.current = restored;
        setState(restored);
        setStorageEstimate(estimate);
        setInitialized(true);
        restored.bundles.forEach((bundle) => {
          const ready = (bundle.items || []).every((item) =>
            [
              PRINT_QUEUE_ITEM_STATES.AWAITING_CONFIRMATION,
              PRINT_QUEUE_ITEM_STATES.SKIPPED,
              PRINT_QUEUE_ITEM_STATES.CANCELED,
            ].includes(item.status),
          );
          if (ready && bundle.items?.length) {
            scheduleBundleAutoClear(bundle.id);
          }
        });
      })
      .catch(() => {
        if (!active) return;
        setPersistenceWarning(
          'Persistent print staging is unavailable. This queue will last only for this tab.',
        );
        setInitialized(true);
      });
    return () => {
      active = false;
      autoClearTimersRef.current.forEach((timer) => clearTimeout(timer));
      autoClearTimersRef.current.clear();
    };
  }, [scheduleBundleAutoClear]);

  const enqueuePackingSetup = useCallback(
    ({
      orders = [],
      format = 'standard',
      formatByOrder = {},
      settings = {},
      copiesByOrder = {},
      qzSettings = {},
      giftCards = [],
      includeSeparators = false,
      source = 'printing-toolkit',
    }) => {
      const packingSettings = withNormalizedPackingSlipMargins(settings);
      const giftByOrder = new Map(
        giftCards.map((card) => [String(card.orderKey || getQueueOrderKey(card.order)), card]),
      );
      const resolveOrderFormat = (orderKey) => {
        const keyed = formatByOrder?.[orderKey];
        if (isPackingSlipFormat(keyed)) return keyed;
        return coercePackingSlipFormat(format);
      };
      const created = orders.map((order) => {
        const orderKey = getQueueOrderKey(order);
        const copies = Math.max(1, Number(copiesByOrder[orderKey]) || 1);
        const orderFormat = resolveOrderFormat(orderKey);
        const items = [];
        if (orderFormat === PACKING_SLIP_FORMAT.CHEETAH) {
          items.push({
            kind: 'cheetah-label',
            profileKey: 'cheetah',
            label: `Order ${orderKey} Cheetah slip and internal label`,
            recipe: {
              settings: {
                ...packingSettings,
                includeDuplicate: false,
              },
              copies,
              orderKey,
              source,
              format: orderFormat,
            },
            routing: qzSettings.cheetah || getDefaultQzSettings('cheetah'),
          });
        } else if (orderFormat === PACKING_SLIP_FORMAT.GO4) {
          items.push({
            kind: 'packing-slip',
            profileKey: 'standard',
            label: `Order ${orderKey} packing slip`,
            recipe: {
              settings: packingSettings,
              copies,
              orderKey,
              source,
              format: orderFormat,
            },
            routing: qzSettings.standard || getDefaultQzSettings('standard'),
          });
          items.push({
            kind: 'go4-outside-label',
            profileKey: 'go4Outside',
            label: `Order ${orderKey} GO4 outside label`,
            recipe: {
              settings: {
                showSignatureLines: packingSettings.showSignatureLines !== false,
              },
              copies,
              orderKey,
              source,
              format: orderFormat,
            },
            routing: qzSettings.go4Outside || getDefaultQzSettings('go4Outside'),
          });
        } else {
          items.push({
            kind: 'packing-slip',
            profileKey: 'standard',
            label: `Order ${orderKey} packing slip`,
            recipe: {
              settings: packingSettings,
              copies,
              orderKey,
              source,
              format: orderFormat,
            },
            routing: qzSettings.standard || getDefaultQzSettings('standard'),
          });
        }
        const gift = giftByOrder.get(orderKey);
        if (gift?.message) {
          items.push({
            kind: 'gift-card',
            profileKey: 'giftCard',
            label: `Order ${orderKey} A6 inscription`,
            recipe: {
              orderKey,
              message: gift.message,
              sourceLabel: gift.sourceLabel,
            },
            routing: withGiftCardTrayRotation(
              qzSettings.giftCard || getDefaultQzSettings('giftCard'),
            ),
          });
        }
        return createPrintQueueBundle(order, items);
      });
      if (includeSeparators) {
        groupOrdersForPrintSeparators(orders).forEach((group) => {
          const lastOrder = group.orders[group.orders.length - 1];
          const key = getQueueOrderKey(lastOrder);
          const bundle = created.find((entry) => entry.orderKey === key);
          if (!bundle) return;
          const groupFormats = new Set(
            (group.orders || []).map((order) => resolveOrderFormat(getQueueOrderKey(order))),
          );
          const mixedFormats = groupFormats.size > 1;
          const allCheetah = groupFormats.size === 1 && groupFormats.has('cheetah');
          const allGo4 = groupFormats.size === 1 && groupFormats.has('go4');
          const formatLabel = mixedFormats
            ? 'Mixed packing formats'
            : allCheetah
              ? 'Cheetah LZ17'
              : allGo4
                ? 'GO4'
                : 'Standard A4';
          bundle.items.push({
            id: `separator-${bundle.id}`,
            kind: 'separator',
            profileKey: 'separator',
            label: `Start of ${group.shippingMethod || 'order group'}`,
            recipe: {
              group: toPersistablePrintSnapshot(group),
              options: {
                outputLabel: mixedFormats
                  ? 'packing output'
                  : allCheetah
                    ? 'Cheetah labels'
                    : 'packing slips',
                formatLabel,
                segmentLabel: group.shippingMethod || 'order group',
                orderCount: group.orders.length,
                labelCount: group.orders.length,
                physicalSheetCount: group.orders.length,
                operator: user?.displayName || user?.email || user?.uid,
              },
            },
            routing: qzSettings.separator || getDefaultQzSettings('separator'),
            status: PRINT_QUEUE_ITEM_STATES.STAGED,
            attempts: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
        });
      }
      commit((current) =>
        created.reduce((next, bundle) => addOrMergePrintBundle(next, bundle), current),
      );
      return created.map((bundle) => bundle.id);
    },
    [commit, user],
  );

  const enqueueWavePickList = useCallback(
    ({ html, label, folderLabel = '', qzSettings = {}, source = 'printing-toolkit' }) => {
      const waveKey = `wave-pick-${String(folderLabel || Date.now()).replace(/\s+/g, '-')}`;
      const bundle = createPrintQueueBundle(
        { source_id: waveKey },
        [
          {
            kind: 'pick-list',
            profileKey: 'standard',
            label: label || `Wave pick list · ${folderLabel || 'current folder'}`,
            recipe: { html: String(html || ''), folderLabel, source },
            routing: qzSettings.standard || getDefaultQzSettings('standard'),
          },
        ],
        { label: label || 'Wave pick list' },
      );
      commit((current) => prependPrintBundle(current, bundle));
      return bundle.id;
    },
    [commit],
  );

  const enqueueGiftCards = useCallback(
    ({ cards = [], qzSettings = {}, includeSeparators = false, source = 'inscriptions' }) => {
      const created = cards.map((card) =>
        createPrintQueueBundle(
          card.order,
          [
            {
              kind: 'gift-card',
              profileKey: 'giftCard',
              label: `Order ${card.orderKey} A6 inscription`,
              recipe: {
                orderKey: card.orderKey,
                message: card.message,
                sourceLabel: card.sourceLabel,
                source,
              },
              routing: withGiftCardTrayRotation(
                qzSettings.giftCard || getDefaultQzSettings('giftCard'),
              ),
            },
          ],
          { label: `Order ${card.orderKey}` },
        ),
      );
      if (includeSeparators) {
        groupItemsForPrintSeparators(cards).forEach((group) => {
          const lastCard = group.items[group.items.length - 1];
          const key = String(lastCard?.orderKey || getQueueOrderKey(lastCard?.order));
          const bundle = created.find((entry) => entry.orderKey === key);
          if (!bundle) return;
          bundle.items.push({
            id: `separator-${bundle.id}`,
            kind: 'separator',
            profileKey: 'separator',
            label: `Start of ${group.shippingMethod || 'order group'} (A6)`,
            recipe: {
              group: toPersistablePrintSnapshot(group),
              options: {
                outputLabel: 'A6 inscription cards',
                formatLabel: 'A6 reverse landscape',
                segmentLabel: group.shippingMethod || 'order group',
                orderCount: group.orders.length,
                labelCount: group.items.length,
                physicalSheetCount: group.items.length,
                operator: user?.displayName || user?.email || user?.uid,
              },
            },
            routing: qzSettings.separator || getDefaultQzSettings('separator'),
            status: PRINT_QUEUE_ITEM_STATES.STAGED,
            attempts: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
        });
      }
      commit((current) =>
        created.reduce((next, bundle) => addOrMergePrintBundle(next, bundle), current),
      );
      return created.map((bundle) => bundle.id);
    },
    [commit, user],
  );

  const enqueueCarrierLabel = useCallback(
    ({ order, blobRef, pageCount = 1, copies = 1, routing, label }) => {
      const orderKey = getQueueOrderKey(order);
      const replacedBlobRef = stateRef.current.bundles
        .find((entry) => entry.orderKey === orderKey && entry.status === 'staged')
        ?.items.find((item) => item.kind === 'carrier-pdf')?.recipe?.blobRef;
      const bundle = createPrintQueueBundle(order, [
        {
          kind: 'carrier-pdf',
          profileKey: 'shippingLabel',
          label: label || `Order ${orderKey} carrier label`,
          recipe: { blobRef, pageCount, copies, orderKey },
          routing: routing || getDefaultQzSettings('shippingLabel'),
        },
      ]);
      commit((current) => addOrMergePrintBundle(current, bundle));
      getPrintQueueStorageEstimate().then(setStorageEstimate).catch(() => {});
      if (replacedBlobRef && replacedBlobRef !== blobRef) {
        deletePrintQueueBlob(replacedBlobRef).catch(() => {});
      }
      return bundle.id;
    },
    [commit],
  );

  /**
   * Append a non-order job to the mixed queue line (PDF / HTML / docx placeholder).
   * Never merges into order bundles — each call adds a new line item for continuity.
   */
  const enqueueGeneralJob = useCallback(
    ({
      kind,
      profileKey,
      label,
      recipe = {},
      routing,
      sizeKey = '',
    }) => {
      const generalKey = `general-${globalThis.crypto?.randomUUID?.() || Date.now()}`;
      const bundle = createPrintQueueBundle(
        { source_id: generalKey },
        [
          {
            kind,
            profileKey,
            label: label || `General ${sizeKey || profileKey}`,
            recipe: { ...recipe, sizeKey, generalKey },
            routing: routing || getDefaultQzSettings(profileKey),
          },
        ],
        { label: label || `General ${sizeKey || profileKey}`, orderKey: generalKey },
      );
      // Force unique key so addOrMerge never collapses general jobs.
      bundle.orderKey = generalKey;
      commit((current) => ({
        ...current,
        bundles: [...current.bundles, bundle],
        updatedAt: new Date().toISOString(),
      }));
      getPrintQueueStorageEstimate().then(setStorageEstimate).catch(() => {});
      return bundle.id;
    },
    [commit],
  );

  const moveBundle = useCallback(
    (fromIndex, toIndex) =>
      commit((current) => ({
        ...current,
        bundles: moveQueueEntry(current.bundles, fromIndex, toIndex),
      })),
    [commit],
  );

  const moveItem = useCallback(
    (bundleId, fromIndex, toIndex) =>
      commit((current) => ({
        ...current,
        bundles: current.bundles.map((bundle) =>
          bundle.id === bundleId
            ? { ...bundle, items: moveQueueEntry(bundle.items, fromIndex, toIndex) }
            : bundle,
        ),
      })),
    [commit],
  );

  const startQueue = useCallback(async () => {
    if (qzPrintExecutor.isRunning()) return;
    commit((current) => ({ ...current, running: true, pauseRequested: false }));
    await qzPrintExecutor.start({
      getSnapshot: () => stateRef.current,
      getNextItem: (snapshot) =>
        getOrderedQueueItems(snapshot).find((item) =>
          [PRINT_QUEUE_ITEM_STATES.STAGED, PRINT_QUEUE_ITEM_STATES.PAUSED].includes(
            item.status,
          ),
        ),
      preflight: async (item) => {
        const readiness = await getQzPrintReadiness({ force: false });
        if (!readiness.ready) throw new Error(readiness.message);
        const diagnostics = diagnoseQzProfiles(
          { [item.profileKey]: item.routing },
          readiness.printerDetails,
          [item.profileKey],
        )[0];
        if (!diagnostics.printer) {
          throw new Error(diagnostics.warnings[0] || 'The queued printer is unavailable.');
        }
        const warningText = diagnostics.warnings.join(' ');
        if (warningText && item.acknowledgedWarningText !== warningText) {
          const warning = new Error(warningText);
          warning.warningOnly = true;
          throw warning;
        }
      },
      materialize: materializePrintQueueItem,
      onItemState: async (itemId, patch) => {
        const next = commit((current) => ({
          ...updateQueueItem(current, itemId, patch),
          activeItemId: [
            PRINT_QUEUE_ITEM_STATES.AWAITING_CONFIRMATION,
            PRINT_QUEUE_ITEM_STATES.PAUSED,
            PRINT_QUEUE_ITEM_STATES.UNCERTAIN,
          ].includes(patch.status)
            ? null
            : itemId,
        }));
        if (patch.status === PRINT_QUEUE_ITEM_STATES.AWAITING_CONFIRMATION) {
          const bundle = next.bundles.find((entry) =>
            entry.items.some((item) => item.id === itemId),
          );
          if (bundle) scheduleBundleAutoClear(bundle.id);
        }
      },
      onRunState: async (patch) => commit((current) => ({ ...current, ...patch })),
    });
  }, [commit, scheduleBundleAutoClear]);

  const pauseQueue = useCallback(
    () => commit((current) => ({ ...current, pauseRequested: true })),
    [commit],
  );

  const resumeQueue = useCallback(() => {
    commit((current) => ({
      ...current,
      pauseRequested: false,
      bundles: current.bundles.map((bundle) => ({
        ...bundle,
        items: bundle.items.map((item) =>
          item.status === PRINT_QUEUE_ITEM_STATES.PAUSED
            ? { ...item, status: PRINT_QUEUE_ITEM_STATES.STAGED, error: '' }
            : item,
        ),
      })),
    }));
    return startQueue();
  }, [commit, startQueue]);

  const resolveUncertain = useCallback(
    (itemId, disposition) => {
      qzPrintExecutor.releaseHeldPayload();
      commit((current) => {
        let next = updateQueueItem(current, itemId, {
          status:
            disposition === 'retry'
              ? PRINT_QUEUE_ITEM_STATES.STAGED
              : disposition === 'produced'
                ? PRINT_QUEUE_ITEM_STATES.AWAITING_CONFIRMATION
                : PRINT_QUEUE_ITEM_STATES.SKIPPED,
          error: '',
        });
        next = { ...next, blockedItemId: null, pauseRequested: disposition !== 'retry' };
        if (disposition === 'abort') {
          next = {
            ...next,
            bundles: next.bundles.map((bundle) => ({
              ...bundle,
              items: bundle.items.map((item) =>
                item.status === PRINT_QUEUE_ITEM_STATES.STAGED
                  ? { ...item, status: PRINT_QUEUE_ITEM_STATES.CANCELED }
                  : item,
              ),
            })),
          };
        }
        return next;
      });
      if (disposition === 'retry') setTimeout(startQueue, 0);
      if (disposition === 'produced') {
        const bundle = stateRef.current.bundles.find((entry) =>
          entry.items.some((item) => item.id === itemId),
        );
        if (bundle) scheduleBundleAutoClear(bundle.id);
      }
    },
    [commit, scheduleBundleAutoClear, startQueue],
  );

  const acknowledgeItemWarning = useCallback(
    (itemId) => {
      const allItems = stateRef.current.bundles.flatMap((bundle) => bundle.items);
      const focused = allItems.find((entry) => entry.id === itemId);
      const warningText = String(focused?.error || '').trim();
      commit((current) => {
        let next = { ...current, pauseRequested: false };
        const liveItems = current.bundles.flatMap((bundle) => bundle.items);
        liveItems.forEach((entry) => {
          const isFocus = entry.id === itemId;
          const isPending =
            entry.status === PRINT_QUEUE_ITEM_STATES.STAGED ||
            entry.status === PRINT_QUEUE_ITEM_STATES.PAUSED;
          if (!isFocus && !isPending) return;
          // One operator acknowledgement covers the same warning for the rest of the run.
          if (
            !isFocus &&
            warningText &&
            entry.acknowledgedWarningText === warningText
          ) {
            return;
          }
          next = updateQueueItem(next, entry.id, {
            status:
              isFocus || entry.status === PRINT_QUEUE_ITEM_STATES.PAUSED
                ? PRINT_QUEUE_ITEM_STATES.STAGED
                : entry.status,
            acknowledgedWarningText: warningText || entry.acknowledgedWarningText || '',
            requiresAcknowledgement: false,
            error: isFocus ? '' : entry.error,
          });
        });
        return next;
      });
      setTimeout(startQueue, 0);
    },
    [commit, startQueue],
  );

  const markItemNotCompleted = useCallback(
    (itemId) =>
      commit((current) => ({
        ...updateQueueItem(current, itemId, {
          status: PRINT_QUEUE_ITEM_STATES.PAUSED,
          error:
            'Marked as not physically completed after the run. Check the printer, then retry this item.',
          physicalPages: 0,
        }),
        pauseRequested: true,
      })),
    [commit],
  );

  const cancelPending = useCallback(
    () =>
      commit((current) => ({
        ...current,
        pauseRequested: true,
        bundles: current.bundles.map((bundle) => ({
          ...bundle,
          items: bundle.items.map((item) =>
            item.status === PRINT_QUEUE_ITEM_STATES.STAGED
              ? { ...item, status: PRINT_QUEUE_ITEM_STATES.CANCELED }
              : item,
          ),
        })),
      })),
    [commit],
  );

  const confirmBundle = useCallback(
    async (bundleId) => {
      clearAutoClearTimer(bundleId);
      const bundle = stateRef.current.bundles.find((entry) => entry.id === bundleId);
      if (!bundle) return;
      const packingItems = bundle.items.filter((item) =>
        ['packing-slip', 'cheetah-label'].includes(item.kind) &&
        [
          PRINT_QUEUE_ITEM_STATES.AWAITING_CONFIRMATION,
          PRINT_QUEUE_ITEM_STATES.CONFIRMED,
        ].includes(item.status),
      );
      try {
        await recordConfirmedPrintQueueBundle(bundle, user?.uid);
        if (packingItems.length && bundle.orderSnapshot) {
          const copies = Math.max(1, Number(packingItems[0].recipe?.copies) || 1);
          await recordPackingSlipPrintCounts([bundle.orderSnapshot], {
            copies,
            copiesByOrder: { [bundle.orderKey]: copies },
            physicalSheetsByOrder: {
              [bundle.orderKey]: Math.max(1, Number(packingItems[0].physicalPages) || 1),
            },
            jobType:
              packingItems[0].kind === 'cheetah-label'
                ? 'cheetah_lz17'
                : packingItems[0].recipe?.format === 'go4'
                  ? 'go4'
                  : 'packing_slip',
            userId: user?.uid,
          });
        }
      } catch (error) {
        commit((current) => ({
          ...current,
          bundles: current.bundles.map((entry) =>
            entry.id === bundleId
              ? {
                  ...entry,
                  auditError:
                    error?.message ||
                    'Output was confirmed but the print audit could not be saved.',
                }
              : entry,
          ),
        }));
        return;
      }
      await Promise.all(
        bundle.items
          .filter((item) => item.recipe?.blobRef)
          .map((item) => deletePrintQueueBlob(item.recipe.blobRef)),
      );
      commit((current) => removeQueueBundle(current, bundleId));
      setStorageEstimate(await getPrintQueueStorageEstimate());
    },
    [clearAutoClearTimer, commit, user?.uid],
  );

  confirmBundleRef.current = confirmBundle;

  const removeBundle = useCallback(
    async (bundleId) => {
      clearAutoClearTimer(bundleId);
      const bundle = stateRef.current.bundles.find((entry) => entry.id === bundleId);
      await Promise.all(
        (bundle?.items || [])
          .filter((item) => item.recipe?.blobRef)
          .map((item) => deletePrintQueueBlob(item.recipe.blobRef)),
      );
      commit((current) => removeQueueBundle(current, bundleId));
      setStorageEstimate(await getPrintQueueStorageEstimate());
    },
    [clearAutoClearTimer, commit],
  );

  const confirmAllReady = useCallback(async () => {
    const readyIds = stateRef.current.bundles
      .filter(
        (bundle) =>
          bundle.items.length > 0 &&
          bundle.items.every((item) =>
            [
              PRINT_QUEUE_ITEM_STATES.AWAITING_CONFIRMATION,
              PRINT_QUEUE_ITEM_STATES.SKIPPED,
              PRINT_QUEUE_ITEM_STATES.CANCELED,
            ].includes(item.status),
          ),
      )
      .map((bundle) => bundle.id);
    for (const bundleId of readyIds) {
      await confirmBundle(bundleId);
    }
  }, [confirmBundle]);

  const clearQueue = useCallback(async () => {
    if (stateRef.current.running) {
      throw new Error('Pause or wait for the current print to finish before clearing the queue.');
    }
    const settleDelayMs = stateRef.current.settleDelayMs;
    const blobRefs = stateRef.current.bundles.flatMap((bundle) =>
      (bundle.items || [])
        .map((item) => item.recipe?.blobRef)
        .filter(Boolean),
    );
    await Promise.all(blobRefs.map((reference) => deletePrintQueueBlob(reference)));
    try {
      await clearPrintQueueStorage();
    } catch {
      // Memory/IndexedDB clear is best-effort; empty state is still applied below.
    }
    const empty = {
      ...createEmptyPrintQueue(),
      settleDelayMs,
    };
    stateRef.current = empty;
    setState(empty);
    try {
      await savePrintQueueState(empty);
      setPersistenceWarning('');
    } catch {
      setPersistenceWarning(
        'The print queue was cleared in this tab but could not be saved. Refresh may restore older staged work.',
      );
    }
    setStorageEstimate(await getPrintQueueStorageEstimate());
  }, []);

  const value = useMemo(
    () => ({
      state,
      initialized,
      storageEstimate,
      persistenceWarning,
      enqueuePackingSetup,
      enqueueWavePickList,
      enqueueGiftCards,
      enqueueCarrierLabel,
      enqueueGeneralJob,
      moveBundle,
      moveItem,
      startQueue,
      pauseQueue,
      resumeQueue,
      cancelPending,
      resolveUncertain,
      acknowledgeItemWarning,
      markItemNotCompleted,
      confirmBundle,
      confirmAllReady,
      removeBundle,
      clearQueue,
      setSettleDelay: (valueMs) =>
        commit((current) => ({
          ...current,
          settleDelayMs: Math.max(0, Math.min(10000, Number(valueMs) || 0)),
        })),
    }),
    [
      state,
      initialized,
      storageEstimate,
      persistenceWarning,
      enqueuePackingSetup,
      enqueueWavePickList,
      enqueueGiftCards,
      enqueueCarrierLabel,
      enqueueGeneralJob,
      moveBundle,
      moveItem,
      startQueue,
      pauseQueue,
      resumeQueue,
      cancelPending,
      resolveUncertain,
      acknowledgeItemWarning,
      markItemNotCompleted,
      confirmBundle,
      confirmAllReady,
      removeBundle,
      clearQueue,
      commit,
    ],
  );

  return <PrintQueueContext.Provider value={value}>{children}</PrintQueueContext.Provider>;
}

export function usePrintQueue() {
  const value = useContext(PrintQueueContext);
  if (!value) throw new Error('usePrintQueue must be used inside PrintQueueProvider.');
  return value;
}

export default PrintQueueContext;
